import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useShell } from "../../shell/context/ShellContext";
import {
  fetchDashboardSummary,
  fetchActiveUsers,
  fetchAzureCosts,
  type DashboardSummary,
  type AzureCostSummary,
} from "../../api/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import styles from "./UsageDashboard.module.css";

const GRANULARITIES = ["hour", "day", "week", "month"] as const;
type Granularity = (typeof GRANULARITIES)[number];

interface PresetRange {
  label: string;
  from: () => string;
  to: () => string;
  defaultGranularity: Granularity;
}

const PRESETS: PresetRange[] = [
  {
    label: "Today",
    from: () => new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
    to: () => new Date().toISOString(),
    defaultGranularity: "hour",
  },
  {
    label: "Last 7 Days",
    from: () => new Date(Date.now() - 7 * 86400000).toISOString(),
    to: () => new Date().toISOString(),
    defaultGranularity: "day",
  },
  {
    label: "Last 30 Days",
    from: () => new Date(Date.now() - 30 * 86400000).toISOString(),
    to: () => new Date().toISOString(),
    defaultGranularity: "day",
  },
  {
    label: "Last 90 Days",
    from: () => new Date(Date.now() - 90 * 86400000).toISOString(),
    to: () => new Date().toISOString(),
    defaultGranularity: "week",
  },
];

const CHART_COLORS = ["#004b87", "#470a68", "#a6192e", "#0078d4", "#107c10", "#ffb900", "#d83b01", "#5c2d91"];
const DASHBOARD_REFRESH_MS = 30_000;
const ACTIVE_USERS_REFRESH_MS = 15_000;

export default function UsageDashboard() {
  const { isAdmin, roleLoaded } = useShell();
  const navigate = useNavigate();

  const [data, setData] = useState<DashboardSummary | null>(null);
  const [costData, setCostData] = useState<AzureCostSummary | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState(false);
  const [activeUsersCount, setActiveUsersCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [activePreset, setActivePreset] = useState(1); // Last 7 Days
  const [granularity, setGranularity] = useState<Granularity>("day");

  // Top Users table sort & pagination
  const [sortField, setSortField] = useState<"userName" | "email" | "queryCount" | "lastActive">("queryCount");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const abortRef = useRef<AbortController | null>(null);

  // Track which filter combo the cost section is currently showing, to trigger reload only on actual changes
  const costFilterRef = useRef<string>("");

  useEffect(() => {
    if (roleLoaded && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [roleLoaded, isAdmin, navigate]);

  const getDateRange = useCallback(() => {
    const preset = PRESETS[activePreset];
    return { from: preset.from(), to: preset.to() };
  }, [activePreset]);

  const loadDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { from, to } = getDateRange();
      const result = await fetchDashboardSummary(from, to, granularity);
      if (!controller.signal.aborted) {
        setData(result);
        setActiveUsersCount(result.activeUsersNow);
        setCurrentPage(1);
      }
    } catch (err: unknown) {
      if (!controller.signal.aborted) {
        setError(err instanceof Error ? err.message : "Failed to load dashboard");
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [getDateRange, granularity]);

  // Cost loader — fires independently, only on filter changes (not auto-refresh)
  const loadCosts = useCallback(async () => {
    const { from, to } = getDateRange();
    setCostLoading(true);
    setCostError(false);
    try {
      const costs = await fetchAzureCosts(from, to, granularity);
      setCostData(costs);
    } catch {
      setCostError(true);
    } finally {
      setCostLoading(false);
    }
  }, [getDateRange, granularity]);

  const loadActiveUsers = useCallback(async () => {
    try {
      const result = await fetchActiveUsers();
      setActiveUsersCount(result.count);
    } catch {
      // Silently fail for active users polling
    }
  }, []);

  // Initial load + reload on filter change
  useEffect(() => {
    if (roleLoaded && isAdmin) {
      loadDashboard();
    }
    return () => abortRef.current?.abort();
  }, [roleLoaded, isAdmin, loadDashboard]);

  // Auto-refresh dashboard data only (NOT costs)
  useEffect(() => {
    if (!roleLoaded || !isAdmin) return;
    const interval = setInterval(() => loadDashboard(true), DASHBOARD_REFRESH_MS);
    return () => clearInterval(interval);
  }, [roleLoaded, isAdmin, loadDashboard]);

  // Active users polling (higher frequency)
  useEffect(() => {
    if (!roleLoaded || !isAdmin) return;
    const interval = setInterval(loadActiveUsers, ACTIVE_USERS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [roleLoaded, isAdmin, loadActiveUsers]);

  // Load costs only when preset or granularity actually changes (not on auto-refresh)
  useEffect(() => {
    if (!roleLoaded || !isAdmin) return;
    const filterKey = `${activePreset}|${granularity}`;
    if (costFilterRef.current === filterKey) return;
    costFilterRef.current = filterKey;
    loadCosts();
  }, [roleLoaded, isAdmin, activePreset, granularity, loadCosts]);

  // Sorted & paginated top users — must be called unconditionally before any early returns
  const sortedUsers = useMemo(() => {
    if (!data) return [];
    const users = [...data.topUsers];
    users.sort((a, b) => {
      let cmp = 0;
      if (sortField === "queryCount") cmp = a.queryCount - b.queryCount;
      else if (sortField === "lastActive") cmp = (a.lastActive ?? "").localeCompare(b.lastActive ?? "");
      else cmp = (a[sortField] ?? "").localeCompare(b[sortField] ?? "");
      return sortDir === "asc" ? cmp : -cmp;
    });
    return users;
  }, [data, sortField, sortDir]);

  if (!roleLoaded) return null;
  if (!isAdmin) return null;

  const handlePresetClick = (index: number) => {
    setActivePreset(index);
    setGranularity(PRESETS[index].defaultGranularity);
  };

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
  const pagedUsers = sortedUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir(field === "queryCount" ? "desc" : "asc"); }
    setCurrentPage(1);
  };

  const sortIndicator = (field: typeof sortField) =>
    sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  // Excel export
  const handleExport = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.queryVolume), "Query Volume");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.uniqueUsersOverTime), "Unique Users");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.responseTimeOverTime), "Response Time");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.modelUsage), "Model Usage");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.productUsage), "Product Usage");
    if (data.sentiment.sentimentOverTime.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.sentiment.sentimentOverTime.map(s => ({
        Period: s.period, Likes: s.value, Dislikes: s.averageValue
      }))), "Sentiment");
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.topUsers.map(u => ({
      Name: u.userName, Email: u.email, Queries: u.queryCount, "Last Active": u.lastActive
    }))), "Top Users");
    if (costData && costData.totalCost > 0) {
      const costSheet = [
        { Metric: "Total Cost", Value: `${costData.totalCost} ${costData.currency}` },
        ...costData.costByService.map(s => ({ Metric: s.serviceName, Value: s.cost, Percentage: `${s.percentage}%` }))
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(costSheet), "Azure Costs");
    }

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "usage-dashboard-export.xlsx");
  };

  const formatSec = (sec: number | null | undefined) => {
    if (sec == null) return "—";
    return `${sec.toFixed(1)}s`;
  };

  const dateRangeLabel = PRESETS[activePreset].label;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Usage Dashboard</h1>
          <p className={styles.subtitle}>System usage analytics and insights</p>
        </div>
        <div className={styles.headerRight}>
          {refreshing && <span className={styles.refreshDot} title="Refreshing..." />}
          <button className={styles.refreshBtn} onClick={handleExport} disabled={!data}>
            Export
          </button>
          <button className={styles.refreshBtn} onClick={() => loadDashboard(true)} disabled={refreshing}>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Date Range</span>
          <div className={styles.presets}>
            {PRESETS.map((p, i) => (
              <button
                key={p.label}
                className={`${styles.presetBtn} ${activePreset === i ? styles.presetBtnActive : ""}`}
                onClick={() => handlePresetClick(i)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Group By</span>
          <div className={styles.granularity}>
            {GRANULARITIES.map((g) => (
              <button
                key={g}
                className={`${styles.granBtn} ${granularity === g ? styles.granBtnActive : ""}`}
                onClick={() => setGranularity(g)}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && !data ? (
        <div className={styles.loadingState}>Loading dashboard...</div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className={styles.kpiRow}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{data.totalQueries.toLocaleString()}</div>
              <div className={styles.kpiLabel}>Total Queries</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{data.uniqueUsers.toLocaleString()}</div>
              <div className={styles.kpiLabel}>Unique Users</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={`${styles.kpiValue} ${styles.kpiLive}`}>{activeUsersCount}</div>
              <div className={styles.kpiLabel}>
                Active Now <span className={styles.liveDot} />
              </div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiValue}>{formatSec(data.averageResponseTimeSec)}</div>
              <div className={styles.kpiLabel}>Avg Response Time</div>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className={styles.chartRow}>
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Query Volume</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.queryVolume}>
                  <defs>
                    <linearGradient id="gradQuery" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#004b87" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#004b87" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke="#004b87" fill="url(#gradQuery)" strokeWidth={2} name="Queries" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Unique Users</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.uniqueUsersOverTime}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#470a68" strokeWidth={2} dot={{ r: 3, fill: "#470a68" }} name="Users" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className={styles.chartRow}>
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>LLM Model Usage</h3>
              {data.modelUsage.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={data.modelUsage}
                      dataKey="count"
                      nameKey="model"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      label={(props: any) => `${props.name || ''} (${props.payload?.percentage ?? 0}%)`}
                    >
                      {data.modelUsage.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>No model usage data available</div>
              )}
            </div>

            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Products Searched</h3>
              {data.productUsage.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.productUsage}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="displayName" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Searches" radius={[4, 4, 0, 0]}>
                      {data.productUsage.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>No product search data available</div>
              )}
            </div>
          </div>

          {/* Charts Row 3: Response Time + Sentiment */}
          <div className={styles.chartRow}>
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Avg Response Time per Query</h3>
              {data.responseTimeOverTime.length > 1 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.responseTimeOverTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="s" />
                    <Tooltip formatter={(v: any) => [`${v}s`, "Avg Response Time"]} />
                    <Line type="monotone" dataKey="averageValue" name="Avg seconds" stroke="#a6192e" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : data.responseTimeOverTime.length === 1 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.responseTimeOverTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit="s" />
                    <Tooltip formatter={(v: any) => [`${v}s`, "Avg Response Time"]} />
                    <Bar dataKey="averageValue" name="Avg seconds" fill="#a6192e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>No response time data available yet</div>
              )}
            </div>

            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>User Sentiment</h3>
              {(data.sentiment.positive > 0 || data.sentiment.negative > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={[
                    { name: "Likes", count: data.sentiment.positive },
                    { name: "Dislikes", count: data.sentiment.negative },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Count" radius={[4, 4, 0, 0]}>
                      <Cell fill="#2e7d32" />
                      <Cell fill="#c62828" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>No feedback data available yet</div>
              )}
            </div>
          </div>

          {/* Top Users Table */}
          <div className={styles.tableCard}>
            <h3 className={styles.chartTitle}>Top Users</h3>
            {data.topUsers.length > 0 ? (
              <>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th style={{ cursor: "pointer" }} onClick={() => handleSort("userName")}>User{sortIndicator("userName")}</th>
                      <th style={{ cursor: "pointer" }} onClick={() => handleSort("email")}>Email{sortIndicator("email")}</th>
                      <th style={{ cursor: "pointer" }} onClick={() => handleSort("queryCount")}>Queries{sortIndicator("queryCount")}</th>
                      <th style={{ cursor: "pointer" }} onClick={() => handleSort("lastActive")}>Last Active{sortIndicator("lastActive")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedUsers.map((u, i) => (
                      <tr key={u.userId}>
                        <td className={styles.rankCell}>{(currentPage - 1) * pageSize + i + 1}</td>
                        <td className={styles.nameCell}>{u.userName}</td>
                        <td className={styles.emailCell}>{u.email}</td>
                        <td className={styles.countCell}>{u.queryCount.toLocaleString()}</td>
                        <td className={styles.dateCell}>
                          {u.lastActive ? new Date(u.lastActive).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div className={styles.pagination}>
                    <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.noData}>No user data available</div>
            )}
          </div>

          {/* Azure Costs */}
          <div className={styles.tableCard}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h3 className={styles.chartTitle}>Azure Costs</h3>
              {costLoading && costData && <span className={styles.refreshDot} title="Refreshing costs..." />}
            </div>
            {costLoading && !costData && (
              <div className={styles.noData}>Loading cost data...</div>
            )}
            {costError && !costData && (
              <div className={styles.noData}>
                Failed to load cost data.{" "}
                <button className={styles.refreshBtn} onClick={loadCosts} style={{ display: "inline", padding: "2px 8px", fontSize: 12 }}>
                  Retry
                </button>
              </div>
            )}
            {costData && costData.totalCost > 0 && (
              <>
                <div className={styles.kpiRow}>
                  <div className={styles.kpiCard}>
                    <div className={styles.kpiValue}>{costData.currency} {costData.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div className={styles.kpiLabel}>Total Cost ({dateRangeLabel})</div>
                  </div>
                </div>

                <div className={styles.chartRow}>
                  <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>Cost Over Time</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={costData.costOverTime}>
                      <defs>
                        <linearGradient id="gradCost" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#107c10" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#107c10" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: any) => [`${costData.currency} ${v}`, "Cost"]} />
                      <Area type="monotone" dataKey="cost" stroke="#107c10" fill="url(#gradCost)" strokeWidth={2} name="Cost" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>Cost by Service</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={costData.costByService.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="serviceName" tick={{ fontSize: 10 }} width={150} />
                      <Tooltip formatter={(v: any) => [`${costData.currency} ${v}`, "Cost"]} />
                      <Bar dataKey="cost" name="Cost" radius={[0, 4, 4, 0]}>
                        {costData.costByService.slice(0, 10).map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
            {!costLoading && !costError && !costData && (
              <div className={styles.noData}>Cost data unavailable</div>
            )}
            {!costLoading && !costError && costData && costData.totalCost === 0 && (
              <div className={styles.noData}>No cost data for the selected period</div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
